import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Contact, ContactChannelType } from '../types/Contact';
import { contactService } from '../services/contactService';
import { contactChannelTypeOptions, contactMatchesChannelTypes, displayContactChannelValue, getChannelSummary, getContactChannelTypeLabel, sortContactChannels } from '../contactChannelUtils';
import { getContactNameEmail, getContactPersonDisplayName } from '../contactNameUtils';
import { Spinner } from '../../../shared/components';
import { Grid } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import styles from './ContactList.module.css';

type SortField = 'name' | 'channels' | 'status';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
    field: SortField;
    direction: SortDirection;
}

export const ContactList: React.FC = () => {
    const { t } = useTranslation(['contacts', 'common']);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canUnmaskChannelValues = hasPermission(PermissionScopes.contactsUnmask);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedChannelTypes, setSelectedChannelTypes] = useState<ContactChannelType[]>([]);
    const [isChannelFilterOpen, setIsChannelFilterOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [maskChannelValues, setMaskChannelValues] = useState(false);
    const [channelValuesRevealed, setChannelValuesRevealed] = useState(true);
    const [maskingBusy, setMaskingBusy] = useState(false);
    const channelFilterRef = useRef<HTMLDivElement | null>(null);
    const didLoadMaskingRef = useRef(false);
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        field: 'name',
        direction: 'asc'
    });

    const channelFilterOptions = contactChannelTypeOptions.map(type => ({
        value: type,
        label: t(`channelTypeOptions.${type}`, { ns: 'contacts' }),
    }));

    const sortContacts = useCallback((contactsToSort: Contact[], config: SortConfig): Contact[] => {
        return [...contactsToSort].sort((a, b) => {
            const valueFor = (contact: Contact) => {
                if (config.field === 'channels') {
                    return getChannelSummary(contact.channels).toLowerCase();
                }

                if (config.field === 'name') {
                    return getContactPersonDisplayName(contact).toLowerCase() || '\uffff';
                }

                return String(contact[config.field] ?? '').toLowerCase();
            };

            const aValue = valueFor(a);
            const bValue = valueFor(b);
            
            if (aValue < bValue) {
                return config.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return config.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, []);

    const applyFiltersAndSort = useCallback((data: Contact[], search: string, channelTypes: ContactChannelType[], sort: SortConfig) => {
        let result = data;

        if (channelTypes.length > 0) {
            result = result.filter(contact => contactMatchesChannelTypes(contact, channelTypes));
        }
        
        if (search.trim()) {
            const searchLower = search.toLowerCase();
            result = result.filter(contact => 
                contact.name.toLowerCase().includes(searchLower) ||
                contact.email.toLowerCase().includes(searchLower) ||
                contact.phone.toLowerCase().includes(searchLower) ||
                contact.whatsApp.toLowerCase().includes(searchLower) ||
                (contact.status || '').toLowerCase().includes(searchLower) ||
                contact.channels.some(channel =>
                    channel.value.toLowerCase().includes(searchLower)
                    || channel.label.toLowerCase().includes(searchLower)
                    || getContactChannelTypeLabel(channel.type).toLowerCase().includes(searchLower))
            );
        }

        result = sortContacts(result, sort);
        setFilteredContacts(result);
    }, [sortContacts]);

    const loadContacts = useCallback(async (channelTypes: ContactChannelType[]) => {
        setLoading(true);
        try {
            const [data, metadata] = await Promise.all([
                contactService.getContacts({
                    channelTypes: channelTypes.length > 0 ? channelTypes : undefined,
                }),
                contactService.getContactMetadata().catch(() => null),
            ]);
            setContacts(data);
            if (metadata) {
                setMaskChannelValues(metadata.maskChannelValues);
                if (!didLoadMaskingRef.current) {
                    didLoadMaskingRef.current = true;
                    setChannelValuesRevealed(!metadata.maskChannelValues);
                }
            }
            setError(null);
        } catch {
            setError(t('failedToLoadContacts', { ns: 'contacts' }));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        void loadContacts(selectedChannelTypes);
    }, [loadContacts, selectedChannelTypes]);

    useEffect(() => {
        applyFiltersAndSort(contacts, searchTerm, selectedChannelTypes, sortConfig);
    }, [contacts, searchTerm, selectedChannelTypes, sortConfig, applyFiltersAndSort]);

    useEffect(() => {
        if (!isChannelFilterOpen) {
            return;
        }

        const handlePointerDown = (event: MouseEvent) => {
            if (channelFilterRef.current && !channelFilterRef.current.contains(event.target as Node)) {
                setIsChannelFilterOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [isChannelFilterOpen]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleChannelTypeToggle = (type: ContactChannelType, checked: boolean) => {
        setSelectedChannelTypes(current => {
            if (checked) {
                return current.includes(type) ? current : [...current, type];
            }

            return current.filter(value => value !== type);
        });
    };

    const handleClearChannelFilters = () => {
        setSelectedChannelTypes([]);
    };

    const handleMaskChannelValuesChange = async (enabled: boolean) => {
        if (!canUnmaskChannelValues || maskingBusy) {
            return;
        }

        setMaskingBusy(true);
        setError(null);
        try {
            const result = await contactService.updateChannelMasking(enabled);
            setMaskChannelValues(result.maskChannelValues);
            setChannelValuesRevealed(!result.maskChannelValues);
            const data = await contactService.getContacts({
                channelTypes: selectedChannelTypes.length > 0 ? selectedChannelTypes : undefined,
            });
            setContacts(data);
        } catch {
            setError(t('failedToUpdateChannelMasking', { ns: 'contacts', defaultValue: 'Could not update channel masking.' }));
        } finally {
            setMaskingBusy(false);
        }
    };

    const handleSort = (field: SortField) => {
        const direction: SortDirection = 
            sortConfig.field === field && sortConfig.direction === 'asc'
                ? 'desc'
                : 'asc';
        
        const newConfig = { field, direction };
        setSortConfig(newConfig);
    };

    const getContactInitials = (name: string) => {
        const parts = name.trim().split(/\s+/).filter(Boolean);
        const initials = parts.slice(0, 2).map(part => part[0]?.toUpperCase() || '').join('');
        return initials || '?';
    };

    const getAvatarToneClass = (name: string) => {
        const tones = [styles.avatarTone1, styles.avatarTone2, styles.avatarTone3, styles.avatarTone4, styles.avatarTone5];
        const sum = Array.from(name).reduce((total, char) => total + char.charCodeAt(0), 0);
        return tones[sum % tones.length];
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
            <Spinner text={t('loadingContacts', { ns: 'contacts' })} />
        </div>
    );
    if (error) return <div className="error">{error}</div>;

    return (
        <div className={styles.page}>
            <div className={styles.headerRow}>
                <div className={styles.titleBlock}>
                    <h2 className={styles.title}>{t('title', { ns: 'contacts' })}</h2>
                    <p className={styles.subtitle}>{t('countLabel', { ns: 'contacts', count: filteredContacts.length })}</p>
                </div>
            </div>

            <div className={styles.toolbar}>
                <div className={styles.searchWrap}>
                    <input
                        type="text"
                        placeholder={t('searchContactsPlaceholder', { ns: 'contacts' })}
                        value={searchTerm}
                        onChange={handleSearch}
                        className={`search-input ${styles.searchInput}`}
                    />
                </div>
                <div className={styles.filtersContainer} ref={channelFilterRef}>
                    <button
                        type="button"
                        className={`btn btn-secondary ${styles.filterButton} ${selectedChannelTypes.length > 0 ? styles.filterButtonActive : ''}`}
                        onClick={() => setIsChannelFilterOpen(current => !current)}
                    >
                        {t('filterByChannel', { ns: 'contacts' })}
                        {selectedChannelTypes.length > 0 ? (
                            <span className={styles.filterCountBadge}>{selectedChannelTypes.length}</span>
                        ) : null}
                    </button>
                    {isChannelFilterOpen ? (
                        <div className={styles.filterMenu}>
                            <div className={styles.filterSectionTitle}>{t('channels', { ns: 'contacts' })}</div>
                            <div className={styles.filterOptionList}>
                                {channelFilterOptions.map(option => (
                                    <label key={option.value} className={styles.filterCheckboxRow}>
                                        <input
                                            className={styles.filterCheckboxInput}
                                            type="checkbox"
                                            checked={selectedChannelTypes.includes(option.value)}
                                            onChange={event => handleChannelTypeToggle(option.value, event.target.checked)}
                                        />
                                        <span className={styles.filterCheckboxLabel}>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                            <div className={styles.filterActions}>
                                <button type="button" className={styles.filterClearBtn} onClick={handleClearChannelFilters}>
                                    {t('clear', { ns: 'common' })}
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
                {canUnmaskChannelValues ? (
                    <label className={styles.maskToggle}>
                        <input
                            type="checkbox"
                            checked={maskChannelValues}
                            disabled={maskingBusy}
                            onChange={event => { void handleMaskChannelValuesChange(event.target.checked); }}
                        />
                        <span>{t('maskChannelValues', { ns: 'contacts', defaultValue: 'Mask channel values' })}</span>
                    </label>
                ) : null}
                {canUnmaskChannelValues && maskChannelValues ? (
                    <button
                        type="button"
                        className={`btn btn-secondary ${styles.revealButton}`}
                        onClick={() => setChannelValuesRevealed(current => !current)}
                    >
                        {channelValuesRevealed
                            ? t('hideChannelValues', { ns: 'contacts', defaultValue: 'Hide values' })
                            : t('showChannelValues', { ns: 'contacts', defaultValue: 'Show values' })}
                    </button>
                ) : null}
                <button
                    className={`btn btn-primary ${styles.addButton}`}
                    type="button"
                    onClick={() => navigate('/contacts/add')}
                >
                    <span aria-hidden>+</span>
                    <span>{t('add', { ns: 'common' })}</span>
                </button>
            </div>

            {filteredContacts.length === 0 ? (
                <div className={styles.emptyState}>{t('empty', { ns: 'contacts' })}</div>
            ) : (
            <Grid
                columns={[
                    {
                        key: 'name',
                        label: t('name', { ns: 'contacts' }),
                        sortable: true,
                        className: styles.nameColumn,
                        headerClassName: styles.nameColumn,
                        render: (c: Contact) => {
                            const personName = getContactPersonDisplayName(c);
                            const listName = personName || t('unnamedContact', { ns: 'contacts' });
                            const email = getContactNameEmail(c);
                            return (
                            <div className={styles.nameCell}>
                                <span className={`${styles.avatar} ${getAvatarToneClass(personName || c.id)}`} aria-hidden>
                                    {getContactInitials(personName)}
                                </span>
                                <div className={styles.nameValue}>
                                    <span className={personName ? styles.primaryText : `${styles.primaryText} ${styles.mutedValue}`}>{listName}</span>
                                    {email ? <span className={styles.secondaryText}>{email}</span> : null}
                                </div>
                            </div>
                            );
                        }
                    },
                    {
                        key: 'channels',
                        label: t('channels', { ns: 'contacts' }),
                        sortable: true,
                        className: styles.channelsColumn,
                        headerClassName: styles.channelsColumn,
                        render: (c: Contact) => {
                            const channels = sortContactChannels(c.channels).filter(channel => channel.value.trim());
                            if (channels.length === 0) {
                                return <span className={`${styles.valueText} ${styles.mutedValue}`}>-</span>;
                            }

                            const visibleChannels = channels.slice(0, 2);
                            const remainingCount = channels.length - visibleChannels.length;

                            return (
                                <div className={styles.channelList}>
                                    {visibleChannels.map(channel => (
                                        <div key={`${channel.id ?? `${channel.type}-${channel.value}`}`} className={styles.channelChip}>
                                            <span className={styles.channelTypeBadge}>{t(`channelTypeOptions.${channel.type}`, { ns: 'contacts' })}</span>
                                            <span className={styles.channelValue}>{displayContactChannelValue(channel.value, { isMasked: channel.isMasked, maskingEnabled: maskChannelValues, revealed: channelValuesRevealed })}</span>
                                        </div>
                                    ))}
                                    {remainingCount > 0 ? <span className={styles.channelMore}>+{remainingCount}</span> : null}
                                </div>
                            );
                        }
                    },
                    {
                        key: 'status',
                        label: t('status', { ns: 'contacts' }),
                        sortable: true,
                        className: styles.compactColumn,
                        headerClassName: styles.compactColumn,
                        render: (c: Contact) => (
                            <span className={c.status ? styles.statusBadge : styles.statusBadgeMuted}>{c.status || '-'}</span>
                        )
                    },
                ]}
                rows={filteredContacts}
                rowKey={(c: Contact) => c.id}
                onRowClick={(c: Contact) => navigate(`/contacts/${c.id}`)}
                sortKey={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={(k) => handleSort(k as SortField)}
            />
            )}
        </div>
    );
};
